import { redirect } from '@tanstack/react-router';
import type { Nivel, ReclamacionesSesion } from '@discopico/domain';
import { puede, type Accion } from './permisos.js';
import { rutaInicialDe } from './sesion.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════════
 *  ESTE GUARDIÁN NO ES SEGURIDAD. Es enrutamiento.
 *
 *  Sirve para que nadie vea una pantalla que le va a dar error en cada botón. La
 *  seguridad son las políticas de seguridad por filas y `app.assert_puede_operar`,
 *  que revalida el estado contra la tabla en cada escritura de dinero.
 *
 *  El escenario "Forzar cambio de contraseña en el primer ingreso" tiene DOS
 *  candados por esta razón: este guardián lleva a la pantalla, y
 *  `app.assert_puede_operar` rechaza toda escritura mientras la bandera esté
 *  arriba. Confiar solo en el enrutamiento sería confiar en código que corre en el
 *  navegador del usuario.
 * ═══════════════════════════════════════════════════════════════════════════════
 */

export type Exigencia = {
  accion?: Accion;
  nivel?: Nivel;
  /** Solo la ruta `/cambiar-password` lo pone en verdadero, para no redirigir en círculo. */
  permitirPasswordPendiente?: boolean;
};

export type ResultadoGuardian =
  | { permitido: true; sesion: ReclamacionesSesion }
  | { permitido: false; hacia: string; motivo: string };

/**
 * La decisión, como función pura. Se separa del `beforeLoad` para poder probarla sin
 * montar un router: las once reglas de este archivo son la parte que se equivoca, y
 * `throw redirect(...)` es la parte que no.
 *
 * El orden de las cuatro verificaciones es el mismo de `app.assert_puede_operar`, y eso
 * importa: la bandera de contraseña temporal pesa más que el rol, así que un
 * administrador recién creado va a cambiar su contraseña antes de ver nada.
 */
export function evaluarGuardian(
  sesion: ReclamacionesSesion | null,
  exigencia: Exigencia,
): ResultadoGuardian {
  if (!sesion) return { permitido: false, hacia: '/ingresar', motivo: 'E_NO_AUTENTICADO' };

  if (sesion.estado === 'bloqueado') {
    return { permitido: false, hacia: '/ingresar', motivo: 'E_CUENTA_BLOQUEADA' };
  }
  if (sesion.estado === 'inactivo') {
    return { permitido: false, hacia: '/ingresar', motivo: 'E_CUENTA_INACTIVA' };
  }

  if (sesion.debeCambiarPassword && exigencia.permitirPasswordPendiente !== true) {
    return { permitido: false, hacia: '/cambiar-password', motivo: 'E_DEBE_CAMBIAR_PASSWORD' };
  }

  // Se devuelve a SU vista y no a una pantalla de error: quien llega a una ruta que no
  // le toca casi siempre llegó por un enlace viejo o por un marcador, no atacando. Y a
  // quien sí esté atacando, la pantalla no le sirve de nada porque la API lo rechaza.
  const suVista = rutaInicialDe(sesion.rol);

  if (exigencia.accion && !puede(sesion.rol, exigencia.accion)) {
    return { permitido: false, hacia: suVista, motivo: 'E_ROL_NO_AUTORIZADO' };
  }

  if (exigencia.nivel !== undefined && sesion.nivel < exigencia.nivel) {
    return { permitido: false, hacia: suVista, motivo: 'E_ROL_NO_AUTORIZADO' };
  }

  return { permitido: true, sesion };
}

/** Lo que un `beforeLoad` recibe de `arbol.ts`. */
export type ContextoRuta = { sesion: ReclamacionesSesion | null };

/**
 * Envoltorio para `beforeLoad`. Lanza la redirección de TanStack Router, que es la
 * forma soportada de cortar la carga de una ruta antes de que se monte un componente.
 */
export function exigir(contexto: ContextoRuta, exigencia: Exigencia = {}): ReclamacionesSesion {
  const r = evaluarGuardian(contexto.sesion, exigencia);
  if (!r.permitido) {
    throw redirect({ to: r.hacia, search: { motivo: r.motivo }, replace: true });
  }
  return r.sesion;
}

/** Atajos legibles para los tres grupos. */
export const exigirSesion = (c: ContextoRuta) => exigir(c, {});
export const exigirAccion = (c: ContextoRuta, accion: Accion) => exigir(c, { accion });
export const exigirNivel = (c: ContextoRuta, nivel: Nivel) => exigir(c, { nivel });
